import { CheckInDevice, DeviceAction, DisplayConfig, TokenExportEntry } from "@/types/CheckInDevice";
import type { ConditionGroup } from "@/types/EventRule";
import { apiGet, apiPost, apiPut, apiDelete, apiPostBlob } from "../_apiShim";

const BASE_PATH = "/api/v1/check-in-devices/";

export async function getDevices(
  eventId: string,
): Promise<CheckInDevice[]> {
  return await apiGet<CheckInDevice[]>(`${BASE_PATH}${eventId}`, {
    "Cache-Control": "no-store",
  }, true);
}

export async function createDevice(
  eventId: string,
  device: {
    name: string;
    categoryId?: string;
    displayConfigOverride?: DisplayConfig;
    canCheckIn?: boolean;
    canCheckOut?: boolean;
    canViewGuestList?: boolean;
    canViewDashboard?: boolean;
    // Per-device override of the category's guest-list permission; absent = inherit.
    canViewGuestListOverride?: boolean;
    canViewGuestDetails?: boolean;
    canEditGuest?: boolean;
    visibleFields?: string[];
    guestFilters?: ConditionGroup;
    visibleOfferingIds?: string[];
    visibleTreatmentIds?: string[];
    visibleRoomIds?: string[];
    actions?: DeviceAction[];
    // DEVICE-WIDE CTA text for the app's guest-detail button — one button fires
    // the whole action list. "" explicitly clears a stored label (the backend
    // mapper skips nulls, so there is no clear-flag).
    actionButtonLabel?: string;
    // Per-device override of the category's actions; absent = inherit.
    actionsOverride?: DeviceAction[];
    sessionTtlHours?: number | null;
    autoCheckoutMinutes?: number | null;
    bypassCapacity?: boolean;
    // BLOCK-mode capacity headroom (rule-gated like bypassCapacity).
    capacityLimit?: number | null;
    canViewAllHistory?: boolean;
    // Walk-up companion (+1) creation from the check-in app.
    canCreateGuest?: boolean;
    // Walk-up GUEST (not on the list) creation from the check-in app.
    canCreateWalkUpGuest?: boolean;
    // Phase E: lift the guest-FIELD cap (see all) vs derive it from the layout.
    allowAllFields?: boolean;
  },
): Promise<CheckInDevice> {
  return await apiPost<CheckInDevice>(
    `${BASE_PATH}${eventId}`,
    device,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function bulkCreateDevices(
  eventId: string,
  data: {
    namePrefix: string;
    count: number;
    categoryId?: string;
    displayConfigOverride?: DisplayConfig;
    canCheckIn?: boolean;
    canCheckOut?: boolean;
    canViewGuestList?: boolean;
    canViewDashboard?: boolean;
    canViewGuestListOverride?: boolean;
    canViewGuestDetails?: boolean;
    canEditGuest?: boolean;
    visibleFields?: string[];
    guestFilters?: ConditionGroup;
    visibleOfferingIds?: string[];
    visibleTreatmentIds?: string[];
    visibleRoomIds?: string[];
    actions?: DeviceAction[];
    // DEVICE-WIDE CTA text for the app's guest-detail button — one button fires
    // the whole action list. "" explicitly clears a stored label (the backend
    // mapper skips nulls, so there is no clear-flag).
    actionButtonLabel?: string;
    actionsOverride?: DeviceAction[];
    sessionTtlHours?: number | null;
    autoCheckoutMinutes?: number | null;
    bypassCapacity?: boolean;
    // BLOCK-mode capacity headroom (rule-gated like bypassCapacity).
    capacityLimit?: number | null;
    canViewAllHistory?: boolean;
    // Walk-up companion (+1) creation from the check-in app.
    canCreateGuest?: boolean;
    // Walk-up GUEST (not on the list) creation from the check-in app.
    canCreateWalkUpGuest?: boolean;
    // Phase E: lift the guest-FIELD cap (see all) vs derive it from the layout.
    allowAllFields?: boolean;
  },
): Promise<CheckInDevice[]> {
  return await apiPost<CheckInDevice[]>(
    `${BASE_PATH}${eventId}/bulk`,
    data,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function updateDevice(
  eventId: string,
  deviceId: string,
  data: {
    checkInDeviceId: string;
    name?: string;
    categoryId?: string;
    clearCategoryId?: boolean;
    displayConfigOverride?: DisplayConfig;
    clearDisplayConfigOverride?: boolean;
    active?: boolean;
    canCheckIn?: boolean;
    canCheckOut?: boolean;
    canViewGuestList?: boolean;
    canViewDashboard?: boolean;
    // Per-device override of the category's guest-list permission + $unset flag.
    canViewGuestListOverride?: boolean;
    clearCanViewGuestListOverride?: boolean;
    canViewGuestDetails?: boolean;
    canEditGuest?: boolean;
    visibleFields?: string[];
    clearVisibleFields?: boolean;
    guestFilters?: ConditionGroup;
    clearGuestFilters?: boolean;
    visibleOfferingIds?: string[];
    visibleTreatmentIds?: string[];
    visibleRoomIds?: string[];
    clearSessionTtlHours?: boolean;
    clearAutoCheckoutMinutes?: boolean;
    clearCapacityLimit?: boolean;
    actions?: DeviceAction[];
    // DEVICE-WIDE CTA text for the app's guest-detail button — one button fires
    // the whole action list. "" explicitly clears a stored label (the backend
    // mapper skips nulls, so there is no clear-flag).
    actionButtonLabel?: string;
    // Per-device override of the category's actions + its $unset clear flag.
    actionsOverride?: DeviceAction[];
    clearActionsOverride?: boolean;
    sessionTtlHours?: number | null;
    autoCheckoutMinutes?: number | null;
    bypassCapacity?: boolean;
    // BLOCK-mode capacity headroom (rule-gated like bypassCapacity).
    capacityLimit?: number | null;
    canViewAllHistory?: boolean;
    // Walk-up companion (+1) creation from the check-in app.
    canCreateGuest?: boolean;
    // Walk-up GUEST (not on the list) creation from the check-in app.
    canCreateWalkUpGuest?: boolean;
    // Phase E: lift the guest-FIELD cap (see all) vs derive it from the layout.
    allowAllFields?: boolean;
  },
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}${eventId}/${deviceId}`,
    data,
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function bulkUpdateDevices(
  eventId: string,
  data: {
    deviceIds: string[];
    categoryId?: string;
    clearCategoryId?: boolean;
    displayConfigOverride?: DisplayConfig;
    clearDisplayConfigOverride?: boolean;
    active?: boolean;
    canCheckIn?: boolean;
    canCheckOut?: boolean;
    canViewGuestList?: boolean;
    canViewDashboard?: boolean;
    // Per-device override of the category's guest-list permission + $unset flag.
    canViewGuestListOverride?: boolean;
    clearCanViewGuestListOverride?: boolean;
    canViewGuestDetails?: boolean;
    canEditGuest?: boolean;
    visibleFields?: string[];
    clearVisibleFields?: boolean;
    guestFilters?: ConditionGroup;
    clearGuestFilters?: boolean;
    visibleOfferingIds?: string[];
    visibleTreatmentIds?: string[];
    visibleRoomIds?: string[];
    clearSessionTtlHours?: boolean;
    clearAutoCheckoutMinutes?: boolean;
    clearCapacityLimit?: boolean;
    actions?: DeviceAction[];
    // DEVICE-WIDE CTA text for the app's guest-detail button — one button fires
    // the whole action list. "" explicitly clears a stored label (the backend
    // mapper skips nulls, so there is no clear-flag).
    actionButtonLabel?: string;
    // Per-device override of the category's actions + its $unset clear flag.
    actionsOverride?: DeviceAction[];
    clearActionsOverride?: boolean;
    sessionTtlHours?: number | null;
    autoCheckoutMinutes?: number | null;
    bypassCapacity?: boolean;
    // BLOCK-mode capacity headroom (rule-gated like bypassCapacity).
    capacityLimit?: number | null;
    canViewAllHistory?: boolean;
    // Walk-up companion (+1) creation from the check-in app.
    canCreateGuest?: boolean;
    // Walk-up GUEST (not on the list) creation from the check-in app.
    canCreateWalkUpGuest?: boolean;
    // Phase E: lift the guest-FIELD cap (see all) vs derive it from the layout.
    allowAllFields?: boolean;
  },
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}${eventId}/bulk`,
    data,
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function deleteDevice(
  eventId: string,
  deviceId: string,
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${eventId}/${deviceId}`, {
    "Cache-Control": "no-store",
  });
}

export async function bulkDeleteDevices(
  eventId: string,
  deviceIds: string[],
): Promise<void> {
  await apiDelete<void>(
    `${BASE_PATH}${eventId}/bulk`,
    {
      "Cache-Control": "no-store",
    },
    { deviceIds },
  );
}

export async function regenerateToken(
  eventId: string,
  deviceId: string,
): Promise<{ token: string }> {
  return await apiPost<{ token: string }>(
    `${BASE_PATH}${eventId}/${deviceId}/regenerate-token`,
    {},
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function exportTokens(
  eventId: string,
  deviceIds: string[],
): Promise<TokenExportEntry[]> {
  return await apiPost<TokenExportEntry[]>(
    `${BASE_PATH}${eventId}/export-tokens`,
    { deviceIds },
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function exportTokensPdf(
  eventId: string,
  deviceIds: string[],
): Promise<Uint8Array> {
  return await apiPostBlob(
    `${BASE_PATH}${eventId}/export-tokens-pdf`,
    { deviceIds },
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function logoutDevice(
  eventId: string,
  deviceId: string,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${eventId}/${deviceId}/logout`,
    {},
    {
      "Cache-Control": "no-store",
    },
  );
}
